import React, { useEffect, useRef, useState } from "react";
import {
  Box, Stack, Typography, TextField, IconButton, CircularProgress, Alert, Chip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Message } from "./useChat.js";
import type { ChatWidgetTheme, SuggestedTrigger } from "@chatfab/shared";

const DEFAULT_THEME: ChatWidgetTheme = {
  primary_color:    "#5C6BC0",
  background_color: "#FFFFFF",
  font_family:      "Inter",
  border_radius:    10,
};

interface Props {
  messages: Message[];
  status: "connecting" | "ready" | "error" | "limit_reached" | "disconnected";
  capMessage: string | null;
  onSend: (text: string) => void;
  botName: string;
  welcomeMessage?: string;
  theme?: ChatWidgetTheme;
  showBranding?: boolean;
  suggestedTriggers?: SuggestedTrigger[];
  onSuggestedTrigger?: (triggerId: string, label: string) => void;
}

function TypingDots() {
  return (
    <Stack direction="row" spacing={0.4} alignItems="center" sx={{ py: 0.5 }}>
      {[0,1,2].map(i => (
        <Box
          key={i}
          sx={{
            width: 6, height: 6, borderRadius: "50%", bgcolor: "text.disabled",
            animation: "bounce 1.2s infinite",
            animationDelay: `${i * 0.2}s`,
            "@keyframes bounce": {
              "0%, 80%, 100%": { transform: "translateY(0)" },
              "40%": { transform: "translateY(-6px)" },
            },
          }}
        />
      ))}
    </Stack>
  );
}

export function ChatWindow({ messages, status, capMessage, onSend, botName, welcomeMessage, theme = DEFAULT_THEME, showBranding = false, suggestedTriggers, onSuggestedTrigger }: Props) {
  const [input, setInput] = useState("");
  const [lastUsedTrigger, setLastUsedTrigger] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const r = theme.border_radius;
  const isDark = theme.dark_mode ?? false;

  // Dynamic colors derived from dark_mode
  const botBubbleBg    = isDark ? "rgba(255,255,255,0.09)" : "#F0F2F5";
  const botBubbleColor = isDark ? "#E8EAF0" : "#1A1A1A";
  const inputBorder    = isDark ? "rgba(255,255,255,0.10)" : "#EEEEEE";
  // Always set an explicit background so the input ignores OS/system dark mode
  const inputFieldBg   = isDark ? "rgba(255,255,255,0.06)" : (theme.background_color ?? "#FFFFFF");
  const inputTextColor = isDark ? "#E8EAF0" : "#1A1A1A";
  const placeholderColor = isDark ? "rgba(255,255,255,0.35)" : "#aaa";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const submit = () => {
    const t = input.trim();
    if (!t || status !== "ready") return;
    onSend(t);
    setInput("");
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  return (
    <Box sx={{
      display: "flex", flexDirection: "column", height: "100%",
      bgcolor: theme.background_color,
      fontFamily: theme.font_family,
    }}>
      {/* Header */}
      <Box sx={{ bgcolor: theme.primary_color, px: 2, py: 1.5, flexShrink: 0 }}>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15, fontFamily: theme.font_family }}>
          {botName}
        </Typography>
        {welcomeMessage && (
          <Typography sx={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontFamily: theme.font_family }}>
            {welcomeMessage}
          </Typography>
        )}
      </Box>

      {/* Status bar */}
      {status === "connecting" && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: "#FFF8E1", display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">Connecting…</Typography>
        </Box>
      )}
      {status === "error" && (
        <Alert severity="error" sx={{ borderRadius: 0, py: 0.5 }}>Connection error — retrying…</Alert>
      )}

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1.5 }}>
        {messages.length === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: "block", textAlign: "center", mt: 4 }}>
            Start the conversation
          </Typography>
        )}
        {messages.map(msg => (
          <Box
            key={msg.id}
            sx={{
              display: "flex",
              justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              mb: 1,
            }}
          >
            <Box sx={{
              maxWidth: "82%",
              bgcolor: msg.role === "user" ? theme.primary_color : botBubbleBg,
              color:   msg.role === "user" ? "#fff" : botBubbleColor,
              borderRadius: `${Math.max(r - 2, 4)}px`,
              px: 1.5, py: 1,
              fontSize: 13,
              fontFamily: theme.font_family,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}>
              {msg.pending && msg.text === "" ? <TypingDots /> : msg.role === "user" ? msg.text : (
                <Markdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => <p style={{ margin: "0 0 0.5em", lineHeight: 1.5 }}>{children}</p>,
                    ul: ({ children }) => <ul style={{ margin: "0.25em 0", paddingLeft: "1.25em" }}>{children}</ul>,
                    ol: ({ children }) => <ol style={{ margin: "0.25em 0", paddingLeft: "1.25em" }}>{children}</ol>,
                    li: ({ children }) => <li style={{ marginBottom: "0.1em" }}>{children}</li>,
                    code: ({ children, className }) => className
                      ? <pre style={{ background: "#1e1e1e", color: "#d4d4d4", padding: "0.5em 0.75em", borderRadius: 6, overflowX: "auto", fontSize: 12, margin: "0.5em 0" }}><code>{children}</code></pre>
                      : <code style={{ background: "rgba(0,0,0,0.08)", borderRadius: 3, padding: "0.1em 0.3em", fontSize: "0.9em" }}>{children}</code>,
                    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#5C6BC0" }}>{children}</a>,
                  }}
                >
                  {msg.text}
                </Markdown>
              )}
            </Box>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>

      {/* Cap warning */}
      {capMessage && (
        <Alert severity="warning" sx={{ borderRadius: 0, py: 0.5, fontSize: 12 }}>{capMessage}</Alert>
      )}

      {/* Suggested trigger chips — clickable shortcuts to configured workflows.
          Rendered above the input on every turn so users always see affordances. */}
      {suggestedTriggers && suggestedTriggers.length > 0 && onSuggestedTrigger && (
        <Box sx={{
          borderTop: `1px solid ${inputBorder}`,
          px: 1.5, py: 0.75, flexShrink: 0,
          display: "flex", gap: 0.75,
          flexWrap: "wrap",
          overflowX: "auto",
        }}>
          {suggestedTriggers.map(s => {
            const used = lastUsedTrigger === s.trigger_id;
            return (
              <Chip
                key={s.trigger_id}
                label={s.label}
                size="small"
                clickable={status === "ready"}
                disabled={status !== "ready"}
                onClick={() => {
                  setLastUsedTrigger(s.trigger_id);
                  onSuggestedTrigger(s.trigger_id, s.label);
                }}
                sx={{
                  fontSize: 12,
                  fontFamily: theme.font_family,
                  borderRadius: `${Math.max(r - 2, 6)}px`,
                  bgcolor: used
                    ? (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)")
                    : "transparent",
                  color: isDark ? "#E8EAF0" : "#1A1A1A",
                  border: `1px solid ${theme.primary_color}`,
                  "& .MuiChip-label": { px: 1.25 },
                  "&:hover": {
                    bgcolor: theme.primary_color,
                    color: "#fff",
                  },
                  transition: "background-color 120ms, color 120ms",
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Input */}
      <Box sx={{ borderTop: `1px solid ${inputBorder}`, px: 1.5, py: 1, flexShrink: 0, display: "flex", gap: 1, alignItems: "flex-end" }}>
        <TextField
          fullWidth multiline maxRows={4} size="small" placeholder="Type a message…"
          value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
          disabled={status === "limit_reached"}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: `${Math.max(r - 2, 6)}px`, fontSize: 13, fontFamily: theme.font_family,
              ...(inputFieldBg && { bgcolor: inputFieldBg }),
              ...(inputTextColor && { color: inputTextColor }),
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: inputBorder,
            },
            "& input, & textarea": {
              color: inputTextColor,
              "&::placeholder": { color: placeholderColor, opacity: 1 },
            },
          }}
        />
        <IconButton
          onClick={submit}
          disabled={!input.trim() || status !== "ready"}
          sx={{
            bgcolor: theme.primary_color, color: "#fff", flexShrink: 0,
            width: 36, height: 36, borderRadius: `${Math.max(r - 2, 6)}px`,
            "&:hover": { bgcolor: theme.primary_color, opacity: 0.88 },
            "&.Mui-disabled": { bgcolor: "#E0E0E0", color: "#9E9E9E" },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {showBranding && (
        <Box sx={{ textAlign: "center", py: 0.5 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            Powered by ChatFab
          </Typography>
        </Box>
      )}
    </Box>
  );
}
